
import { TopMenu } from "@/components/TopMenu";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

type CallHistoryRecord = {
  id: string;
  created_at: string;
  call_time: string;
  status: string;
  call_outcome: string | null;
  recording_link: string | null;
  subscriber_id: string;
};

export default function CallHistory() {
  const [calls, setCalls] = useState<CallHistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const checkAuthAndFetchCalls = async () => {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.log('No authenticated user found, redirecting to register');
          navigate('/register');
          return;
        }

        // First get the user's subscriber_id from user_psid_mappings
        const { data: psidData, error: psidError } = await supabase
          .from('user_psid_mappings')
          .select('subscriber_id')
          .eq('user_email', user.email)
          .single();

        if (psidError) {
          console.error('Error fetching subscriber_id:', psidError);
          if (psidError.code === 'PGRST116') {
            toast({
              title: "Registration Required",
              description: "Please complete your registration to view call history.",
            });
            navigate('/register');
          }
          return;
        }

        if (!psidData || !psidData.subscriber_id) {
          console.log('No subscriber_id found, redirecting to registration');
          toast({
            title: "Registration Required",
            description: "Please complete your registration to view call history.",
          });
          navigate('/register');
          return;
        }

        // Then get the call history for that subscriber_id
        const { data: callData, error: callError } = await supabase
          .from('call_history')
          .select('*')
          .eq('subscriber_id', psidData.subscriber_id)
          .order('created_at', { ascending: false });

        if (callError) {
          console.error('Error fetching call history:', callError);
          throw callError;
        }
        
        if (!callData || callData.length === 0) {
          // No actual call data exists yet, show empty state
          setCalls([]);
        } else {
          // Map the call data to our component state
          const mappedCallData = callData.map(call => ({
            ...call,
            status: call.status
          }));
          
          setCalls(mappedCallData);
        }

      } catch (error) {
        console.error('Error fetching calls:', error);
        toast({
          title: "Error",
          description: "There was a problem loading your call history.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndFetchCalls();
  }, [navigate, toast]);


  const getStatusBadge = (status: string) => {
    const statusStyles: { [key: string]: string } = {
      Connected: "bg-green-100 text-green-800", 
      Missed: "bg-red-100 text-red-800",
      "No Answer": "bg-red-100 text-red-800",
      Engaged: "bg-orange-100 text-orange-800",
    };

    return (
      <Badge className={statusStyles[status] || "bg-gray-100 text-gray-800"}> {/* Default gray */}
        {status}
      </Badge>
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(calls.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCalls = calls.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <TopMenu />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading call history...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopMenu />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Call History ({calls.length})</h1>
        <div className="space-y-4">
          {currentCalls.map((call) => (
            <div
              key={call.id}
              className="bg-card text-card-foreground rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <p className="font-medium">{new Date(call.call_time).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                    {getStatusBadge(call.status)}
                  </div>
                  {call.status !== 'No Answer' && (
                    <p className="text-sm text-muted-foreground">
                      Duration: {new Date(call.call_time).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                  {call.call_outcome && (
                    <p className="text-sm text-muted-foreground">{call.call_outcome}</p>
                  )}
                </div>
                {call.recording_link && call.status !== 'Missed' ? (
                  <audio
                    controls
                    className="h-8 w-[84]"
                    src={call.recording_link}
                    onError={(e) => {
                      console.error('Audio playback error:', e);
                      // Hide the audio player on error
                      (e.target as HTMLAudioElement).style.display = 'none';
                      toast({
                        title: "Playback Error",
                        description: "Unable to play the recording. The file might be unavailable.",
                        variant: "destructive",
                      });
                    }}
                  >
                    Your browser does not support the audio element.
                  </audio>
                ) : null}
              </div>
            </div>
          ))}
          {calls.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              No calls in your history yet.
            </div>
          )}
        </div>
        
        {calls.length > itemsPerPage && (
          <div className="mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => handlePageChange(currentPage - 1)}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => handlePageChange(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                
                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(currentPage + 1)}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
}
